import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { showInDevFeatures } from './buildTimeReplacedValues';
import { BreadcrumbTrailComponent } from './content/breadcrumb-trail/breadcrumb-trail.component';
import { FooterComponent } from './content/footer/footer.component';
import { HeaderCtaComponent } from './content/header-cta/header-cta.component';
import { HeaderComponent } from './content/header/header.component';
import { PageCtaComponent } from './content/page-cta/page-cta.component';
import { PageExploreComponent } from './content/page-explore/page-explore.component';
import { PageHeadingComponent } from './content/page-heading/page-heading.component';
import { GrooveCandidateSwitcherComponent } from './groove-candidate-switcher/groove-candidate-switcher.component';
import { GrooveCandidateService } from './groove-candidate.service';
import { RouteUiDataService } from './route-ui-data';
import { ScrollMemoryService } from './scroll-memory.service';
import { MobileDrawerComponent } from './site-shell/mobile-drawer/mobile-drawer.component';
import { TopNavComponent } from './site-shell/top-nav/top-nav.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    PageCtaComponent,
    PageExploreComponent,
    PageHeadingComponent,
    TopNavComponent,
    MobileDrawerComponent,
    HeaderCtaComponent,
    GrooveCandidateSwitcherComponent,
    BreadcrumbTrailComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  protected readonly showInDevFeatures = showInDevFeatures;
  protected readonly drawerOpen = signal(false);
  protected readonly grooveCandidate = inject(GrooveCandidateService);
  private readonly routeUiData = inject(RouteUiDataService);
  // When the active route hosts its own stage extension (home's hero), the
  // shell suppresses the default chrome→paper handoff — that route owns
  // extending the stage and rendering its own handoff. Default (flag absent)
  // = the shell renders the handoff for the standard inner-page composition.
  protected readonly stageExtension = this.routeUiData.stageExtension;
  // When the active route declares suppressHeaderCta: true (home,
  // x-ploration-jazz), the in-header CTA strip is hidden for the entire
  // route — that route's own hero already carries call/email conversion
  // actions, so the header strip's "Questions about lessons?" prompt would
  // be redundant.
  protected readonly suppressHeaderCta = this.routeUiData.suppressHeaderCta;
  // When the active route declares suppressFooterCta: true (home), the
  // shell-rendered Explore tiles + bottom CTA pair is hidden — home renders its
  // own inline Explore section and reprise CTA instead.
  protected readonly suppressFooterCta = this.routeUiData.suppressFooterCta;
  // `'article'` on `/blog/:slug` (spec §14.2 item 1) applies `content--article`
  // to `<main>`, capping the heading/trail/bottom-CTA to the article measure.
  protected readonly measure = this.routeUiData.measure;
  // The visible breadcrumb trail: static `data.breadcrumbs` or a runtime
  // override (dynamic blog routes). SEOService's BreadcrumbList JSON-LD is
  // the same list only for static routes; `/blog/:slug` builds its own
  // JSON-LD separately, and the topic/tag/level override adds a leaf crumb
  // here that their `data.breadcrumbs`-driven JSON-LD omits.
  protected readonly breadcrumbs = this.routeUiData.breadcrumbs;
  private readonly mobileDrawer =
    viewChild.required<MobileDrawerComponent>('mobileDrawer');

  constructor() {
    // Eagerly instantiated for its side effect only (never stored — see
    // provideCustomProviders' identical SEOService injection): its
    // constructor subscribes to router.events, and that subscription must be
    // live for every navigation, including the very first one. Wiring it
    // here (never from a template) is what the app-level constructor buys —
    // see ScrollMemoryService's doc comment for why subscription ORDER among
    // router.events listeners matters and is fixed once established.
    inject(ScrollMemoryService);

    // Fragment-anchor settle for DIRECT navigation. The router's one-shot
    // anchorScrolling fires at navigation end — before this app's deferred
    // sections and webfonts have laid out — so on long pages the target is
    // missed entirely (then scroll restoration pins the page at 0). In-page
    // fragment CLICKS are unaffected (content already rendered). Fix: after
    // hydration, retry the anchor scroll until the target stops moving (or
    // ~3s), yielding immediately if the user takes over.
    afterNextRender(() => {
      if (!location.hash) return;
      const id = location.hash.slice(1);
      let userScrolled = false;
      const mark = () => (userScrolled = true);
      window.addEventListener('wheel', mark, { once: true, passive: true });
      window.addEventListener('touchstart', mark, {
        once: true,
        passive: true,
      });
      let attempts = 0;
      let lastTop: number | undefined;
      const settle = () => {
        if (userScrolled || attempts >= 12) return;
        attempts += 1;
        const target = document.getElementById(id);
        if (target) {
          const top = Math.round(target.getBoundingClientRect().top);
          // Out of place -> scroll. In place AND layout stable -> done.
          if (Math.abs(top) > 4) {
            target.scrollIntoView();
            lastTop = undefined; // re-confirm stability after moving
          } else if (lastTop !== undefined && Math.abs(top - lastTop) <= 1) {
            return;
          }
          lastTop = top;
        }
        setTimeout(settle, 250);
      };
      settle();
    });
  }

  // Public so the template can bind to it directly. Also exercised
  // unit-side from app.component.spec to bypass the hamburger button
  // (which is display:none above sm and not clickable at the default
  // test viewport).
  openMobileDrawer(): void {
    this.mobileDrawer().open();
    this.drawerOpen.set(true);
  }
}
